import React, { useState } from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  LocationOn,
  Bed,
  Bathroom,
  SquareFoot,
  Edit,
  Delete,
  Visibility,
  Favorite,
  Share,
  ImageNotSupported,
} from '@mui/icons-material';
import { Property } from '@/types';
import PropertyCardSkeleton from './PropertyCardSkeleton';
import { useAuthStore } from '@/store/authStore';
import { AuthService } from '@/services/auth';

export interface PropertyCardProps {
  property?: Property;
  loading?: boolean;
  onView?: (property: Property) => void;
  onEdit?: (property: Property) => void;
  onDelete?: (property: Property) => void;
  showActions?: boolean;
  compact?: boolean;
  orientation?: 'vertical' | 'horizontal';
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  loading = false,
  onView,
  onEdit,
  onDelete,
  showActions = true,
  compact = false,
  orientation = 'vertical',
}) => {
  const [imgError, setImgError] = useState(false);
  const { user } = useAuthStore();

  if (loading) {
    return <PropertyCardSkeleton showActions={showActions} compact={compact} orientation={orientation} />;
  }

  if (!property) {
    return null;
  }

  const isFavorite = user?.profile.favorites?.includes(property.id) || false;

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) return;

    try {
      let updatedFavorites = user.profile.favorites || [];
      if (isFavorite) {
        updatedFavorites = updatedFavorites.filter(id => id !== property.id);
      } else {
        updatedFavorites = [...updatedFavorites, property.id];
      }

      await AuthService.updateUserProfile(user.id, {
        ...user.profile,
        favorites: updatedFavorites
      });
      // Force store refresh would be ideal, but for now it'll be updated on next fetch
      // or we can update local store
      useAuthStore.setState({ user: { ...user, profile: { ...user.profile, favorites: updatedFavorites } } });
    } catch (err) {
      console.error('Failed to toggle favorite', err);
    }
  };

  const isHorizontal = orientation === 'horizontal';
  const primaryImage = property.metadata.images?.[0] || '/placeholder-property.jpg';
  const price = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: property.metadata.currency || 'USD',
    minimumFractionDigits: 0,
  }).format(property.metadata.price);

  const handleView = () => {
    if (onView) {
      onView(property);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(property);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(property);
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: isHorizontal ? 'row' : 'column',
        cursor: onView ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': onView
          ? {
              transform: isHorizontal ? 'translateX(4px)' : 'translateY(-4px)',
              boxShadow: 4,
            }
          : {},
      }}
      onClick={handleView}
    >
      <Box 
        sx={{ 
          position: 'relative', 
          width: isHorizontal ? { xs: 120, sm: 200, md: 280 } : '100%',
          height: isHorizontal ? 'auto' : (compact ? 180 : 240),
          bgcolor: 'grey.100',
          flexShrink: 0,
        }}
      >
        {!imgError ? (
          <CardMedia
            component="img"
            height="100%"
            image={primaryImage}
            alt={property.metadata.title}
            onError={() => setImgError(true)}
            sx={{ objectFit: 'cover', height: '100%' }}
          />
        ) : (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.disabled',
              flexDirection: 'column',
              p: 2,
              textAlign: 'center'
            }}
          >
            <ImageNotSupported fontSize={isHorizontal ? "medium" : "large"} />
            {!isHorizontal && (
              <Typography variant="caption" sx={{ mt: 1 }}>
                Image not available
              </Typography>
            )}
          </Box>
        )}

        {property.isFeatured && (
          <Chip
            label="Featured"
            color="primary"
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
            }}
          />
        )}
        {!property.isActive && (
          <Chip
            label="Inactive"
            color="default"
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 1,
            }}
          />
        )}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: property.isFeatured ? 80 : 8,
            display: 'flex',
            gap: 0.5,
            zIndex: 1,
            opacity: isHorizontal ? 0 : 1, // Hide by default on list view to reduce clutter, show on hover could be an option
            transition: 'opacity 0.2s',
            '.MuiCard-root:hover &': {
              opacity: 1,
            },
          }}
        >
          <Tooltip title={isFavorite ? "Remove favorite" : "Favorite"}>
            <IconButton
              size="small"
              aria-label="Add to favorites"
              sx={{ bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
              onClick={handleToggleFavorite}
            >
              <Favorite fontSize="small" color={isFavorite ? "error" : "inherit"} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Share">
            <IconButton
              size="small"
              aria-label="Share property"
              sx={{ bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
              onClick={(e) => {
                e.stopPropagation();
                // Add share handler logic here
              }}
            >
              <Share fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <CardContent sx={{ flexGrow: 1, pb: isHorizontal ? 1 : 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography variant="h6" component="h3" gutterBottom noWrap title={property.metadata.title} sx={{ maxWidth: '70%' }}>
              {property.metadata.title}
            </Typography>
            <Typography
              variant="h6"
              color="primary"
              fontWeight="bold"
            >
              {price}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary' }}>
            <LocationOn fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="body2" noWrap>
              {property.location.city}, {property.location.state}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mt: isHorizontal ? 1 : 2,
              mb: isHorizontal ? 1 : 0,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} title="Bedrooms">
              <Bed fontSize="small" color="action" />
              <Typography variant="body2">{property.metadata.bedrooms}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} title="Bathrooms">
              <Bathroom fontSize="small" color="action" />
              <Typography variant="body2">{property.metadata.bathrooms}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} title="Square Footage">
              <SquareFoot fontSize="small" color="action" />
              <Typography variant="body2">
                {property.metadata.squareFootage.toLocaleString()} sq ft
              </Typography>
            </Box>
          </Box>

          {(!compact || isHorizontal) && (
            <Box sx={{ mt: 2 }}>
              <Chip
                label={property.metadata.propertyType}
                size="small"
                sx={{ mr: 0.5, mb: 0.5, textTransform: 'capitalize' }}
              />
              <Chip
                label={property.metadata.listingType}
                size="small"
                color="secondary"
                sx={{ mr: 0.5, mb: 0.5, textTransform: 'capitalize' }}
              />
            </Box>
          )}

          {property.metadata.description && (!compact || isHorizontal) && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 1,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {property.metadata.description}
            </Typography>
          )}
        </CardContent>

        {showActions && (
          <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Visibility />}
              onClick={handleView}
              aria-label="View property details"
            >
              View
            </Button>
            <Box>
              {onEdit && (
                <Tooltip title="Edit">
                  <IconButton 
                    size="small" 
                    onClick={handleEdit} 
                    color="primary"
                    aria-label="Edit property"
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              )}
              {onDelete && (
                <Tooltip title="Delete">
                  <IconButton 
                    size="small" 
                    onClick={handleDelete} 
                    color="error"
                    aria-label="Delete property"
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </CardActions>
        )}
      </Box>
    </Card>
  );
};

export default PropertyCard;
